import { useEffect, useState } from "react";
import { tr } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { addDays, backendWeekday, fmtDate, fromISO, toISO, todayTR } from "@/lib/helpers";

const calClasses = {
  head_cell: "text-navy/60 rounded-md w-10 font-bold text-[0.75rem]",
  day: "h-10 w-10 p-0 rounded-full font-semibold text-navy hover:bg-bej transition-colors aria-selected:opacity-100",
  day_selected: "!bg-navy !text-white hover:!bg-navy",
  day_today: "ring-2 ring-bej-dark",
  day_disabled: "!text-slate-300 line-through hover:!bg-transparent cursor-not-allowed",
  caption_label: "font-heading font-extrabold text-navy",
  cell: "relative p-0 text-center text-sm",
};

export function useDisabledDays(site) {
  const today = todayTR();
  const max = addDays(today, site?.booking_days_ahead || 60);
  const holidays = new Set((site?.holidays || []).map((h) => h.date));
  return (d) => {
    const iso = toISO(d);
    const wh = site?.working_hours?.[String(backendWeekday(d))];
    return iso < today || iso > max || holidays.has(iso) || !wh?.open;
  };
}

function SlotPicker({ date, value, onChange }) {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    if (!date) return;
    setSlots(null);
    api.get(`/availability?date=${date}`).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [date]);
  if (!date) return <p className="text-sm text-slate-500">Önce bir gün seçin.</p>;
  if (slots === null) return <p className="text-sm text-slate-500">Saatler yükleniyor…</p>;
  if (!slots.length) return <p className="text-sm text-rose-600" data-testid="no-slots">Bu gün için uygun saat kalmadı. Başka bir gün seçin.</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slot-list">
      {slots.map((s) => (
        <button key={s} type="button" onClick={() => onChange(s)} data-testid={`slot-${s.replace(":", "")}`}
          className={`rounded-xl py-2.5 text-sm font-bold transition-colors duration-150 ${value === s ? "bg-navy text-white" : "bg-bej-light text-navy hover:bg-bej"}`}>
          {s}
        </button>
      ))}
    </div>
  );
}

export function StepOne({ courses, site, form, set, onNext }) {
  const disabled = useDisabledDays(site);
  const ready = form.course_id && form.date && form.time;
  return (
    <div className="space-y-8">
      <div>
        <label className="block font-heading font-bold text-navy mb-2">Kurs</label>
        <Select value={form.course_id} onValueChange={(v) => set({ course_id: v })}>
          <SelectTrigger className="h-12 rounded-xl text-base" data-testid="booking-course-select">
            <SelectValue placeholder="Bir kurs seçin" />
          </SelectTrigger>
          <SelectContent>
            {courses.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`booking-course-option-${c.slug}`}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <label className="block font-heading font-bold text-navy mb-2">Gün</label>
          <div className="rounded-2xl border border-bej bg-white inline-block" data-testid="booking-calendar">
            <Calendar mode="single" locale={tr} weekStartsOn={1} showOutsideDays={false}
              selected={form.date ? fromISO(form.date) : undefined}
              onSelect={(d) => d && set({ date: toISO(d), time: "" })}
              disabled={disabled} fromDate={fromISO(todayTR())} today={fromISO(todayTR())} classNames={calClasses} />
          </div>
        </div>
        <div>
          <label className="block font-heading font-bold text-navy mb-2">
            Saat {form.date && <span className="font-body font-semibold text-sm text-slate-500">· {fmtDate(form.date)}</span>}
          </label>
          <SlotPicker date={form.date} value={form.time} onChange={(t) => set({ time: t })} />
          <p className="mt-4 text-xs text-slate-500">Saatler Türkiye saatine (İstanbul) göredir. Kapalı günler ve geçmiş saatler seçilemez.</p>
        </div>
      </div>
      <button type="button" disabled={!ready} onClick={onNext} data-testid="booking-next-btn"
        className="w-full sm:w-auto rounded-full bg-navy px-8 py-3.5 font-heading font-bold text-white transition-[background-color,opacity] hover:bg-navy-dark disabled:opacity-40 disabled:cursor-not-allowed">
        Devam et
      </button>
    </div>
  );
}
